import React, { useEffect, useRef, useState } from "react";

import Button from "@/components/Atoms/Button";
import Slider from "@/components/Atoms/Slider";
import Typography from "@/foundation/Typography/V2";

import SlickArrowButton from "@/components/Molecules/SlickArrowButtonV1";

import RecipeCard from "@/containers/RecipeListPage/RecipeContainer/RecipeCard";
import { mapRecipes } from "@/containers/RecipeListPage/mapRecipeListPageData";

import { classNameGenerator, clsx } from "@/utils";

import { useAppDispatch } from "@/hooks";
import * as actions from "@/redux/RecipeReducer";

import styles from "./RecipesCarousel.module.scss";
import Loader from "@/components/Atoms/Loader";

interface RecipesCarouselProps {}

const RecipesCarousel: React.FC<RecipesCarouselProps> = () => {
  const sliderRef = useRef(null);
  const dispatch = useAppDispatch();

  const collections = [
    {
      value: "collection-healthy-evening-snacks",
      label: "Healthy Evening Snacks",
    },
    { value: "collection-pcos", label: "PCOS" },
    { value: "collection-juices-beverages", label: "Juices Beverages" },
    { value: "collection-kids-diabetes", label: "Kids Diabetes" },
    { value: "collection-diabetes", label: "Diabetes" },
    { value: "collection-healthy-mid-day", label: "Healthy Mid Day" },
    { value: "collection-healthy-dinner", label: "Healthy Dinner" },
    { value: "collection-gut-health", label: "Gut Health" },
    { value: "collection-healthy-salads", label: "Healthy Salads" },
    { value: "attribute-high-fibre", label: "High Fibre" },
    { value: "attribute-summer-refreshers", label: "Summer Refreshers" },
  ];

  const [isLoading, setIsLoading] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [activeCollection, setActiveCollection] = useState(
    collections?.[0]?.value || ""
  );

  useEffect(() => {
    setIsLoading(true);
    let limit = 9;
    if (activeCollection?.startsWith("attribute-")) {
      const id = activeCollection?.replace("attribute-", "");
      dispatch(actions.getRecipeByAttributeSlug({ id, limit })).then((res) => {
        const data = res?.payload?.data?.results?.data?.map(mapRecipes);
        setIsLoading(false);
        setRecipes(data);
      });
    } else {
      const id = activeCollection?.replace("collection-", "");
      dispatch(actions.getRecipeByCollectionSlug({ id, limit })).then((res) => {
        const data = res?.payload?.data?.results?.data?.map(mapRecipes);
        setIsLoading(false);
        setRecipes(data);
      });
    }
  }, [dispatch, activeCollection]);

  const settings = {
    autoplay: true,
    autoplaySpeed: 3000,
    dots: false,
    arrows: true,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          autoplay: true,
          autoplaySpeed: 3000,
          arrows: false,
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: true,
          centerPadding: "30px",
        },
      },
    ],
    prevArrow: <SlickArrowButton buttonType="prev" icon={recipePrevious} />,
    nextArrow: <SlickArrowButton buttonType="next" icon={recipeNext} />,
  };

  const next = () => {
    sliderRef.current.slickNext();
  };

  const previous = () => {
    sliderRef.current.slickPrev();
  };

  const cls = classNameGenerator(styles);

  const header = (
    <div className={cls("header")}>
      <Typography variant="heading2" className={cls("title")}>
        Similar Tasty Recipes for your Health Goals
      </Typography>
      <Typography
        variant="link1"
        className={cls("browseAll")}
        href={`/recipes/${activeCollection}`}
      >
        Browse All
      </Typography>
    </div>
  );

  const collection = (
    <div className={cls("collections")}>
      {collections?.map(({ value, label }, index) => (
        <div
          key={index}
          className={clsx(
            cls("chip"),
            activeCollection === value ? cls("active") : ""
          )}
          onClick={() => setActiveCollection(value)}
        >
          {label}
        </div>
      ))}
    </div>
  );

  const actionButtons = (
    <div className={cls("actionButtons")}>
      <Button variant="icon" icon={recipePrevious} onClick={previous} />
      <Button variant="icon" icon={recipeNext} onClick={next} />
    </div>
  );

  return (
    <section className={cls("recipesSection")}>
      <div className={cls("recipesSectionContainer")}>
        <div className={cls("topSection")}>
          {header}
          {collection}
        </div>
        {isLoading ? (
          <div className={cls("loader")}>
            <Loader />
          </div>
        ) : (
          <Slider ref={sliderRef} settings={settings}>
            {recipes?.map((recipe, index) => (
              <RecipeCard key={index} data={recipe} />
            ))}
          </Slider>
        )}
        {actionButtons}
      </div>
    </section>
  );
};

export default RecipesCarousel;

const recipePrevious = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="61"
    height="62"
    viewBox="0 0 61 62"
    fill="none"
  >
    <path
      d="M38.125 46.25L22.875 31L38.125 15.75"
      stroke="black"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const recipeNext = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="61"
    height="62"
    viewBox="0 0 61 62"
    fill="none"
  >
    <path
      d="M22.875 46.25L38.125 31L22.875 15.75"
      stroke="black"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
